/**
 * Wer in „Zeiten und Spesen" einen Monatsblock bekommt, und wie der Monat
 * Tag für Tag aussieht.
 *
 * Eigene Datei ohne Supabase, damit `npm run logik` beides ohne Browser und
 * ohne Server prüfen kann (`pruefung/spesenmonat.test.ts`). Aus demselben
 * Grund steht der Import von `kalender.ts` mit Endung: Node lädt die Datei
 * im Test direkt, ohne Vite, und rät keine Endungen.
 *
 * ═══ WARUM NICHT MEHR „JEDER AKTIVE FAHRER" (HJ-011) ═══
 *
 * Seit HJ-011 ist ein Fahrer ab dem Anlegen aktiv, nicht erst nach dem
 * Einlösen des Codes. „Jeder aktive Fahrer bekommt einen Block" hieß damit:
 * auch, wer die App noch nie geöffnet hat — mit einem Monat voller
 * „fehlt"-Tage, die er gar nicht hätte erfassen können. Eine Warnung, die bei
 * jedem Neuen kommt, liest man nach dem dritten Mal nicht mehr. Deshalb jetzt:
 * aktiv UND App eingerichtet (`auth_user_id`), oder Tage in diesem Monat. Das
 * Zweite hält wie bisher auch Ausgeschiedene und Bürokräfte in dem Monat, in
 * dem sie gearbeitet haben.
 */

import { alsTag } from './kalender.ts';

/** Was `monatDurchgehen` von einem Tag braucht — `Spesentag` erfüllt es. */
export type Tagesspur = { datum: string; uebernachtung: boolean };

export type MonatseintragVon<T> =
  | { art: 'reise'; datum: string; gruppe: T[] }
  | { art: 'tag'; datum: string; tag: T }
  | { art: 'luecke'; datum: string; werktag: boolean };

export type Blockperson = {
  rolle: string;
  status: string;
  auth_user_id: string | null;
  eintritt: string | null;
};

export function bekommtBlock(
  person: Blockperson,
  hatTageImMonat: boolean,
  monatsEnde: string,
): boolean {
  if (hatTageImMonat) return true;
  // Wer erst nach diesem Monat anfängt, hat darin nichts versäumt.
  if (person.eintritt !== null && person.eintritt > monatsEnde) return false;
  return (
    person.rolle === 'fahrer' &&
    person.status === 'aktiv' &&
    person.auth_user_id !== null
  );
}

/**
 * Ein einzelner Tag ohne Übernachtung ist keine Reise, sondern ein Arbeitstag.
 * Bekäme jeder Tag einen Rahmen, sagte der Rahmen nichts mehr aus.
 */
export function istReise(gruppe: Tagesspur[]): boolean {
  return gruppe.length > 1 || gruppe.some((t) => t.uebernachtung);
}

/**
 * Aus Tagen und Reisen die vollständige Monatsliste bauen.
 *
 * ═══ WARUM BIS HEUTE UND NICHT BIS ZUM MONATSLETZTEN ═══
 *
 * Ein Arbeitstag in der Zukunft lässt sich nicht erfassen — die Datenbank
 * weist ihn ab (`arbeitstag_frist_pruefen`). Stünde der Rest des Monats
 * trotzdem als Lückenreihe da, wären es lauter Zeilen mit einem Knopf, der
 * nicht funktionieren kann. Im laufenden August endet die Liste also heute; im
 * Juli geht sie bis zum 31.
 *
 * ═══ WARUM EINE REISE NIE EINE LÜCKE ENTHÄLT ═══
 *
 * Eine Reise verlangt aufeinanderfolgende Kalendertage — das prüft schon die
 * Datenbanksicht über `kam_von_unterwegs`. Ein fehlender Tag beendet sie.
 * Deshalb genügt es, beim Durchgehen den ersten Tag einer Reise zu erkennen
 * und die übrigen zu überspringen.
 *
 * ═══ VOR DEM EINTRITT GIBT ES KEINE LÜCKE ═══
 *
 * Wer am 17. anfängt, hat vom 1. bis 16. nichts versäumt. Diese Tage stehen
 * gar nicht erst da — auch nicht leise, denn ein „Nachtragen"-Knopf für einen
 * Tag vor dem Arbeitsvertrag lädt zu genau dem Fehler ein. Ein ERFASSTER Tag
 * davor bleibt aber stehen: Dann stimmt das Eintrittsdatum nicht, und ein
 * Tag, der auf der Lohnabrechnung steht, darf nicht verschwinden.
 */
export function monatDurchgehen<T extends Tagesspur>(
  tage: T[],
  gruppen: T[][],
  monatsAnfang: string,
  letzterTag: string,
  eintritt: string | null = null,
): MonatseintragVon<T>[] {
  const nachDatum = new Map(tage.map((t) => [t.datum, t]));

  /** Datum des ersten Tages → die Reise, die dort anfängt. */
  const reiseAb = new Map<string, T[]>();
  /** Alle Tage, die zu einer Reise gehören — sie bekommen keine eigene Zeile. */
  const imVerbund = new Set<string>();
  for (const gruppe of gruppen) {
    if (!istReise(gruppe)) continue;
    reiseAb.set(gruppe[0].datum, gruppe);
    for (const tag of gruppe) imVerbund.add(tag.datum);
  }

  const eintraege: MonatseintragVon<T>[] = [];
  const lauf = new Date(monatsAnfang + 'T12:00:00');
  const ende = new Date(letzterTag + 'T12:00:00');

  while (lauf <= ende) {
    const datum = alsTag(lauf);
    const reise = reiseAb.get(datum);

    if (reise) {
      eintraege.push({ art: 'reise', datum, gruppe: reise });
    } else if (!imVerbund.has(datum)) {
      const tag = nachDatum.get(datum);
      if (tag) {
        eintraege.push({ art: 'tag', datum, tag });
      } else if (eintritt === null || datum >= eintritt) {
        // Wochentag 0 ist Sonntag, 6 der Samstag. Beide sind ohne Eintrag der
        // Normalfall und zählen deshalb nicht in die Lückenzahl — angezeigt
        // werden sie trotzdem, denn gefahren wird auch samstags.
        const wochentag = lauf.getDay();
        eintraege.push({
          art: 'luecke',
          datum,
          werktag: wochentag >= 1 && wochentag <= 5,
        });
      }
    }

    lauf.setDate(lauf.getDate() + 1);
  }

  return eintraege;
}

/* ═══ KENNZEICHEN „NACHGETRAGEN" / „GEÄNDERT" (HJ-030) ═══
 *
 * Die Zeile eines Tages zeigt, ob er nachträglich angefasst wurde — der Tag
 * ist Lohngrundlage. Dabei NICHT jeder Protokolleintrag: Der Auslöser
 * `protokolliere_arbeitstag` schreibt auch das ganz normale Setzen des
 * Feierabends in der App mit (Ende: leer → 16:00), und „Tag nachgetragen"
 * bei jedem vergangenen Tag, also auch, wenn das Handy im Funkloch erst am
 * nächsten Morgen zustellt. Stünde dann an fast jedem Tag ein Kennzeichen,
 * sagte es nichts mehr (Review HJ-030).
 *
 * Gezählt wird deshalb, was jemand ANDERES als die Person des Tages getan hat
 * (das Büro), und das Überschreiben einer schon vorhandenen Zeit — egal von
 * wem. Ein unbekannter Bearbeiter zählt wie ein anderer: lieber sichtbar.
 */
export type Protokollspur = { feld: string; alt: string | null; durch: string | null };

export function kennzeichenFuer(
  eintraege: Protokollspur[],
  personDesTages: string,
): 'nachgetragen' | 'geändert' | null {
  const vonAnderen = (e: Protokollspur) => e.durch !== personDesTages;
  if (eintraege.some((e) => e.feld === 'Tag nachgetragen' && vonAnderen(e))) {
    return 'nachgetragen';
  }
  if (eintraege.some((e) => e.feld !== 'Tag nachgetragen' && (e.alt !== null || vonAnderen(e)))) {
    return 'geändert';
  }
  return null;
}

/**
 * Unter dieser Dauer ist ein Tag fast sicher kein Arbeitstag, sondern ein
 * Test oder eine Fehlbedienung (App kurz geöffnet, gleich wieder beendet).
 * Markiert, nicht ausgeblendet: Ob er stimmt, entscheidet das Büro (HJ-029).
 */
export const SEHR_KURZ_MINUTEN = 15;

export function sehrKurz(dauerMinuten: number | null): boolean {
  return dauerMinuten !== null && dauerMinuten < SEHR_KURZ_MINUTEN;
}
